import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { createDiseaseEncyclopediaDetailUrl } from '@/entities/disease-encyclopedia/util';
import useGetKeywordIntegratedSearchDiseaseEncyclopedia, {
  EKeywordSearchDiseaseEncyclopediaSortType,
} from '@/hooks/search/useGetKeywordIntegratedSearchDiseaseEncyclopedia';
import useCustomRouter from '@/hooks/useCustomRouter';
import { ROUTES } from '@/types/common';
import { SearchResult } from '@/types/search';
import { useRef, useState } from 'react';

export interface DiseaseEncyclopediaSearchViewModel {
  keywordSearchDiseaseEncyclopedia?: SearchResult<DiseaseEncyclopediaModel>;
  keyword: string;
  searchInputRef: React.RefObject<HTMLInputElement>;
  currentPage: number;
  queries: { keyword: string; page: string };
  totalPage: number;
  totalCount: number;
  handleSubmit: () => void;
  handleResetSearchKeyword: () => void;
  handleBackButtonClick: () => void;
  onClickDiseaseEncyclopedia: (diseaseEncyclopedia: DiseaseEncyclopediaModel) => () => void;
  handleChangeSearchKeyword: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const useDiseaseEncyclopediaSearchViewModel = (): DiseaseEncyclopediaSearchViewModel => {
  const { router } = useCustomRouter();
  const queries = router.query as { keyword: string; page: string };

  const currentPage = Number(queries.page || '1');
  const [keyword, setKeyword] = useState(queries.keyword || '');

  const searchInputRef = useRef<HTMLInputElement>(null);

  const { keywordSearchDiseaseEncyclopedia, totalPage, totalCount } = useGetKeywordIntegratedSearchDiseaseEncyclopedia({
    keyword: queries.keyword || '',
    enabled: !!queries.keyword,
    limit: 10,
    page: currentPage,
    sortType: EKeywordSearchDiseaseEncyclopediaSortType.Score,
  });

  const handleSubmit = () => {
    const searchKeyword = keyword.trim();
    if (searchKeyword) {
      router.replace({
        pathname: ROUTES.DISEASE_ENCYCLOPEDIA.SEARCH.path,
        query: { keyword: searchKeyword },
      });
    }
  };

  const handleResetSearchKeyword = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    router.push({
      pathname: ROUTES.DISEASE_ENCYCLOPEDIA.HOME.path,
      query: {},
    });
  };

  const handleBackButtonClick = () => {
    router.back();
  };

  const onClickDiseaseEncyclopedia = (diseaseEncyclopedia: DiseaseEncyclopediaModel) => () => {
    router.push(createDiseaseEncyclopediaDetailUrl(diseaseEncyclopedia));
  };

  const handleChangeSearchKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  return {
    keyword,
    keywordSearchDiseaseEncyclopedia,
    searchInputRef,
    currentPage,
    queries,
    totalPage,
    totalCount,
    handleSubmit,
    handleResetSearchKeyword,
    handleBackButtonClick,
    onClickDiseaseEncyclopedia,
    handleChangeSearchKeyword,
  };
};

export default useDiseaseEncyclopediaSearchViewModel;
